import React, { useState } from 'react';
import { useAuth } from '@es-tools/shared';
import Header from '../components/Header';
import AppCard from '../components/AppCard';
import BrandFilter from '../components/BrandFilter';
import WelcomeHero from '../components/WelcomeHero';
import UpdatesFeed from '../components/UpdatesFeed';
import MissionStatement from '../components/MissionStatement';

type Brand = 'all' | 'mars-men' | 'hollow-socks' | 'easy-street';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedBrand, setSelectedBrand] = useState<Brand>('all');

  // Real applications and tools
  const apps = [
    // Easy Street main account
    {
      id: 'es-aws-console',
      name: 'AWS Console - Easy Street',
      description: 'Access AWS Management Console for Easy Street main account (217553593646)',
      icon: '☁️',
      url: 'https://217553593646.signin.aws.amazon.com/console',
      status: 'active' as const,
      brands: ['easy-street'],
      category: 'Infrastructure',
      lastUpdated: '2024-01-20',
      external: true,
    },
    // Mars Men specific apps
    {
      id: 'marsmen-aws-console',
      name: 'AWS Console - Mars Men',
      description: 'Access AWS Management Console for Mars Men account (631046354185)',
      icon: '☁️',
      url: 'https://631046354185.signin.aws.amazon.com/console',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Infrastructure',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'marsmen-review-automation',
      name: 'Mars Men - Review Management',
      description: 'AI-powered review analysis and creative highlight extraction for Mars Men products',
      icon: '⭐',
      url: 'https://marsmen.automations.easystreetbrands.com/reviews',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Analytics & Reviews',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'marsmen-analytics',
      name: 'Mars Men - Analytics Dashboard',
      description: 'Sales and performance metrics for Mars Men brand',
      icon: '📊',
      url: 'https://auto-marsmen.easystreetbrands.com/analytics',
      status: 'coming-soon' as const,
      brands: ['mars-men'],
      category: 'Analytics & Reviews',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'marsmen-creative-analytics',
      name: 'Mars Men - Creative Analytics',
      description: 'Executive creative performance dashboard for strategist output, agency quality, and quarterly pacing.',
      icon: '🎯',
      url: 'https://marsmen.automations.easystreetbrands.com/creative-analytics',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Analytics & Reviews',
      lastUpdated: '2026-03-09',
      external: true,
    },
    {
      id: 'marsmen-superset',
      name: 'Mars Men - Superset',
      description: 'Interactive reporting tool for Mars Men dashboards, ad hoc analysis, and deeper operational reporting.',
      icon: '📎',
      url: 'http://superset-new-alb-868358265.us-east-1.elb.amazonaws.com/',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Analytics & Reviews',
      lastUpdated: '2026-03-09',
      external: true,
    },
    {
      id: 'data-platform-guide',
      name: 'Data Platform Guide',
      description: 'Team-facing guide to source systems, warehouse families, reporting surfaces, and Claude Code analytics access.',
      icon: '🧭',
      url: '/data-platform',
      status: 'active' as const,
      brands: ['all'],
      category: 'Infrastructure',
      lastUpdated: '2026-03-24',
      external: false,
    },
    {
      id: 'marsmen-dashboard-viewer',
      name: 'Mars Men - Dashboard Viewer',
      description: 'One workspace for Mars Men dashboards across internal apps, Google Sheets, Notion, Superset, and external reporting surfaces.',
      icon: '🗂️',
      url: 'https://automations.mengotomars.com/dashboard-viewer/',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Analytics & Reviews',
      lastUpdated: '2026-06-24',
      external: true,
    },
    {
      id: 'marsmen-subscription-intelligence',
      name: 'Mars Men - Subscription Intelligence',
      description: 'Returning revenue forecasting with visible retention assumptions, Finance scenarios, and frozen planning snapshots.',
      icon: '🔁',
      url: 'https://automations.mengotomars.com/subscription-intelligence/forecasting',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Analytics & Reviews',
      lastUpdated: '2026-07-28',
      external: true,
    },
    {
      id: 'marsmen-churn-dashboard',
      name: 'Mars Men - Churn Analytics',
      description: 'Cohort retention analysis and churn prediction dashboard for Mars Men subscriptions',
      icon: '📈',
      url: 'https://marsmen.automations.easystreetbrands.com/churn-dashboard',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Analytics & Reviews',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'marsmen-mission-control',
      name: 'Mars Men - Mission Control',
      description: 'Operational dashboard for order management, rules, and insights',
      icon: '🚀',
      url: 'https://marsmen.automations.easystreetbrands.com/mission-control',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Operations',
      lastUpdated: '2024-08-25',
      external: true,
    },
    {
      id: 'marsmen-partnership-ops',
      name: 'Mars Men - Partnership Ops',
      description: 'Influencer and partner onboarding, campaign assignments, deliverables, product seeding, reviews, and payout readiness.',
      icon: '🤝',
      url: 'https://automations.mengotomars.com/influencer-manager',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Operations',
      lastUpdated: '2026-05-04',
      external: true,
    },
    {
      id: 'marsmen-nano-banana',
      name: 'Mars Men - Nano Banana',
      description: 'AI-powered image generation and editing for marketing materials',
      icon: '🎨',
      url: 'https://marsmen.automations.easystreetbrands.com/nano-banana',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Creative Tools',
      lastUpdated: '2024-09-15',
      external: true,
    },
    {
      id: 'marsmen-video-briefs',
      name: 'Mars Men - Video Brief Automation',
      description: 'Gemini-assisted video transcript + creative brief generator with Notion publishing',
      icon: '🎬',
      url: 'https://marsmen.automations.easystreetbrands.com/video-briefs',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Creative Tools',
      lastUpdated: '2025-10-28',
      external: true,
    },
    {
      id: 'marsmen-video-image-generation',
      name: 'Mars Men - Video & Image Generation',
      description: 'Unified studio for WaveSpeed longform plus Veo, Runway, Flux, and Suno models',
      icon: '🎥',
      url: 'https://marsmen.automations.easystreetbrands.com/video-image-generation',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Creative Tools',
      lastUpdated: '2025-11-10',
      external: true,
    },
    {
      id: 'marsmen-ai-leaderboard',
      name: 'Mars Men - AI Leaderboard',
      description: 'Company-wide Anthropic leaderboard for output, streaks, and team momentum.',
      icon: '🏁',
      url: 'https://automations.mengotomars.com/leaderboard',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Analytics & Reviews',
      lastUpdated: '2026-04-03',
      external: true,
    },
    
    // Hollow Socks specific apps
    {
      id: 'hollow-aws-console',
      name: 'AWS Console - Hollow Socks',
      description: 'Access AWS Management Console for Hollow Socks account (282304105893)',
      icon: '☁️',
      url: 'https://282304105893.signin.aws.amazon.com/console',
      status: 'active' as const,
      brands: ['hollow-socks'],
      category: 'Infrastructure',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'hollow-review-automation',
      name: 'Hollow Socks - Review Management',
      description: 'AI-powered review analysis and creative highlight extraction for Hollow Socks products',
      icon: '⭐',
      url: 'https://hollow.automations.easystreetbrands.com/reviews',
      status: 'active' as const,
      brands: ['hollow-socks'],
      category: 'Analytics & Reviews',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'hollow-analytics',
      name: 'Hollow Socks - Analytics Dashboard',
      description: 'Sales and performance metrics for Hollow Socks brand',
      icon: '📊',
      url: 'https://auto-hollow.easystreetbrands.com/analytics',
      status: 'coming-soon' as const,
      brands: ['hollow-socks'],
      category: 'Analytics & Reviews',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'hollow-nano-banana',
      name: 'Hollow Socks - Nano Banana',
      description: 'AI-powered image generation and editing for marketing materials',
      icon: '🎨',
      url: 'https://hollow.automations.easystreetbrands.com/nano-banana',
      status: 'active' as const,
      brands: ['hollow-socks'],
      category: 'Creative Tools',
      lastUpdated: '2024-09-15',
      external: true,
    },
    {
      id: 'hollow-video-briefs',
      name: 'Hollow Socks - Video Brief Automation',
      description: 'Queue Drive footage and scripts to auto-build paid-social creative briefs in Notion',
      icon: '🎬',
      url: 'https://hollow.automations.easystreetbrands.com/video-briefs',
      status: 'active' as const,
      brands: ['hollow-socks'],
      category: 'Creative Tools',
      lastUpdated: '2025-10-28',
      external: true,
    },
    {
      id: 'hollow-video-image-generation',
      name: 'Hollow Socks - Video & Image Generation',
      description: 'Unified studio for WaveSpeed longform plus Veo, Runway, Flux, and Suno models',
      icon: '🎥',
      url: 'https://hollow.automations.easystreetbrands.com/video-image-generation',
      status: 'active' as const,
      brands: ['hollow-socks'],
      category: 'Creative Tools',
      lastUpdated: '2025-11-10',
      external: true,
    },
    {
      id: 'n8n-mars-men',
      name: 'n8n Workflows - Mars Men',
      description: 'Automation workflows and integrations for Mars Men operations',
      icon: '⚡',
      url: 'https://marsmen.app.n8n.cloud/',
      status: 'active' as const,
      brands: ['mars-men'],
      category: 'Automation',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'n8n-hollow-socks',
      name: 'n8n Workflows - Hollow Socks',
      description: 'Automation workflows and integrations for Hollow Socks operations',
      icon: '⚡',
      url: 'https://easystreetbrands.app.n8n.cloud/',
      status: 'active' as const,
      brands: ['hollow-socks'],
      category: 'Automation',
      lastUpdated: '2024-01-20',
      external: true,
    },
    {
      id: 'github-repo',
      name: 'GitHub Repository',
      description: 'Source code and infrastructure for all Easy Street automation tools',
      icon: '🐙',
      url: 'https://github.com/easystreetbrands/easy-street-automation-infrastructure',
      status: 'active' as const,
      brands: ['all'],
      category: 'Development',
      lastUpdated: '2024-01-20',
      external: true,
    },
  ];

  const updates = [
    {
      date: '2026-05-04',
      title: 'Mars Men Partnership Ops Added',
      description: 'Influencer onboarding, campaign assignments, deliverable review, product seeding, and payout readiness now have a dedicated Mars Men workspace.',
      type: 'launch' as const,
    },
    {
      date: '2025-10-28',
      title: 'Video Brief Automation (Mars Men + Hollow)',
      description: 'Automated Drive → Gemini → Notion pipeline is live with SPA preview & publish flow.',
      type: 'launch' as const,
    },
    {
      date: '2024-09-15',
      title: 'Nano Banana Image Platform',
      description: 'Launched AI-powered image generation and editing platform for Mars Men and Hollow Socks marketing materials.',
      type: 'launch' as const,
    },
    {
      date: '2024-01-21',
      title: 'GitHub Repository Access',
      description: 'Added direct access to the Easy Street automation infrastructure repository for code management and collaboration.',
      type: 'feature' as const,
    },
    {
      date: '2024-01-20',
      title: 'n8n Workflows Integration',
      description: 'Connected n8n automation platforms for both Mars Men and Hollow Socks brands.',
      type: 'feature' as const,
    },
    {
      date: '2024-01-20',
      title: 'Review Automation Launched',
      description: 'Our first automation tool is now live! Extract valuable insights from customer reviews.',
      type: 'launch' as const,
    },
    {
      date: '2024-01-18',
      title: 'Portal Goes Live',
      description: 'ES Automation Portal is officially launched for Easy Street Brands team members.',
      type: 'launch' as const,
    },
  ];

  const filteredApps = apps.filter(app => {
    if (selectedBrand === 'all') return true;
    return app.brands.includes(selectedBrand);
  });

  const activeToolCount = apps.filter(app => app.status === 'active').length;
  const categories = [...new Set(filteredApps.map(app => app.category))];

  return (
    <div className="dashboard">
      <Header />

      <WelcomeHero userName={user?.name?.split(' ')[0] || 'there'} />

      <main className="dashboard-main">
        <div className="dashboard-container">
          <MissionStatement />

          <div className="dashboard-grid">
            <div className="main-content">
              <div className="section-header">
                <h2>Available Tools</h2>
                <BrandFilter 
                  selectedBrand={selectedBrand}
                  onBrandChange={setSelectedBrand}
                />
              </div>

              {categories.map(category => (
                <div key={category} className="category-section">
                  <h3 className="category-title">{category}</h3>
                  <div className="apps-grid">
                    {filteredApps
                      .filter(app => app.category === category)
                      .map((app) => (
                        <AppCard key={app.id} app={app} />
                      ))}
                  </div>
                </div>
              ))}

              {filteredApps.length === 0 && (
                <div className="empty-state">
                  <p>No tools available for the selected brand yet.</p>
                  <p className="empty-state-sub">Check back soon as we build more automation tools!</p>
                </div>
              )}

              <div className="coming-soon-section">
                <h3 className="category-title">Coming Soon</h3>
                <div className="coming-soon-grid">
                  <div className="coming-soon-card">
                    <span className="coming-icon">✍️</span>
                    <h4>Ad Copy Generator</h4>
                    <p>Generate compelling ad copy using customer language</p>
                  </div>
                  <div className="coming-soon-card">
                    <span className="coming-icon">📧</span>
                    <h4>Email Automator</h4>
                    <p>Automated email sequences based on customer behavior</p>
                  </div>
                  <div className="coming-soon-card">
                    <span className="coming-icon">📦</span>
                    <h4>Inventory Forecaster</h4>
                    <p>ML-powered inventory prediction and optimization</p>
                  </div>
                </div>
              </div>
            </div>

            <aside className="sidebar">
              <UpdatesFeed updates={updates} />
              
              <div className="quick-stats">
                <h3>Platform Stats</h3>
                <div className="stat-item">
                  <span className="stat-value">{activeToolCount}</span>
                  <span className="stat-label">Active Tools</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">3</span>
                  <span className="stat-label">AWS Accounts</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">3</span>
                  <span className="stat-label">Brands Supported</span>
                </div>
              </div>

              <div className="help-section">
                <h3>Need Help?</h3>
                <p>Contact the automation team for support or to request new tools.</p>
                <a href="mailto:automation@easystreetbrands.com" className="help-link">
                  Get Support
                </a>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <p>© 2024 Easy Street Brands. Building the future of e-commerce automation.</p>
          <div className="footer-links">
            <a href="https://easystreetbrands.com" target="_blank" rel="noopener noreferrer">Easy Street</a>
            <span className="separator">•</span>
            <a href="https://mengotomars.com" target="_blank" rel="noopener noreferrer">Mars Men</a>
            <span className="separator">•</span>
            <a href="https://hollowsocks.com" target="_blank" rel="noopener noreferrer">Hollow Socks</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
